import { z } from 'zod';
import { ErrorCode } from './api';
import { FilterNodeSchema } from './filter';
import { Uuid } from './ids';
import { SourceId } from './values';

/** Minimal reference to a record, used for relation values and links. */
export const RecordRef = z.object({ id: Uuid, objectKey: z.string(), title: z.string() });
export type RecordRef = z.infer<typeof RecordRef>;

/** Where the current value of a field came from (value_sources). */
export const ValueSourceInfo = z.object({
  source: z.string(),
  label: z.string(),
  kind: z.enum(['manual', 'external', 'computed']),
  observedAt: z.string(),
  confidence: z.number().nullable(),
  locked: z.boolean(),
  value: z.unknown(),
});
export type ValueSourceInfo = z.infer<typeof ValueSourceInfo>;

export const FieldSources = z.object({
  current: ValueSourceInfo.nullable(),
  candidates: z.array(ValueSourceInfo),
});
export type FieldSources = z.infer<typeof FieldSources>;

/**
 * A record as returned by the API. `values` is keyed by field api key. Relation fields hold a
 * RecordRef (single side) or RecordRef[] (many side).
 */
export const RecordDTO = z.object({
  id: Uuid,
  objectKey: z.string(),
  objectTypeId: Uuid,
  title: z.string(),
  values: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  /** Present when requested with `include=sources`. Keyed by field api key. */
  sources: z.record(z.string(), FieldSources).optional(),
});
export type RecordDTO = z.infer<typeof RecordDTO>;

export const CreateRecordInput = z.object({
  values: z.record(z.string(), z.unknown()).default({}),
  /** Provenance of the written values (default `manual`). */
  source: SourceId.optional(),
  sourceLabel: z.string().max(120).optional(),
});
export type CreateRecordInput = z.input<typeof CreateRecordInput>;

export const UpdateRecordInput = z.object({
  values: z.record(z.string(), z.unknown()),
  /** Optimistic concurrency: fails with 409 version_conflict if the record changed. */
  expectedVersion: z.number().int().positive().optional(),
  source: SourceId.optional(),
  sourceLabel: z.string().max(120).optional(),
});
export type UpdateRecordInput = z.input<typeof UpdateRecordInput>;

export const RecordLink = z.object({
  id: Uuid,
  relationId: Uuid,
  /** Relation field api key on the record the link was requested from. */
  field: z.string(),
  record: RecordRef,
  /** Link field values keyed by link field api key (e.g. role, influence, sentiment). */
  data: z.record(z.string(), z.unknown()),
  position: z.number().int(),
  createdAt: z.string(),
});
export type RecordLink = z.infer<typeof RecordLink>;

export const CreateLinkInput = z.object({
  targetId: Uuid,
  data: z.record(z.string(), z.unknown()).default({}),
  /** Optimistic concurrency on the record the link is added to. */
  expectedVersion: z.number().int().positive().optional(),
});
export type CreateLinkInput = z.input<typeof CreateLinkInput>;

export const UpdateLinkInput = z.object({ data: z.record(z.string(), z.unknown()) });
export type UpdateLinkInput = z.infer<typeof UpdateLinkInput>;

/** One entry of a record's change history (record_changes). */
export const RecordChange = z.object({
  id: Uuid,
  recordId: Uuid,
  op: z.enum(['create', 'update', 'delete', 'restore', 'link', 'unlink']),
  changes: z.array(
    z.object({
      field: z.string(),
      fieldId: z.string(),
      label: z.string(),
      from: z.unknown(),
      to: z.unknown(),
    }),
  ),
  actor: z.object({ type: z.string(), id: z.string().nullable(), label: z.string().nullable() }),
  source: z.string().nullable(),
  causationId: z.string().nullable(),
  correlationId: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type RecordChange = z.infer<typeof RecordChange>;

// -------------------------------------------------------------------------------------------------
// Record mutation contract (record-ops, plan §8.8 and §7.3): expected versions, undo tokens, bulk
// writes with per-record results, history restore.
// -------------------------------------------------------------------------------------------------

/** How long an undo token can be used. */
export const UNDO_TTL_MS = 10 * 60_000;
/** Bulk operations (and undos) above this many records run as a background job. */
export const BULK_SYNC_LIMIT = 1000;
/** Most records one bulk operation may select (by filter). */
export const BULK_MAX_RECORDS = 100_000;
/** Most ids in an `{ ids }` selection or rows in a `cells` paste. */
export const BULK_MAX_IDS = 10_000;

/** Optimistic concurrency: the version the caller last saw (`RecordDTO.version`). */
export const ExpectedVersion = z.number().int().positive();

/** What an undo token reverts, for clients that word their own toast. */
export const UndoSummary = z.object({
  kind: z.enum([
    'create',
    'update',
    'stage',
    'delete',
    'restore',
    'link',
    'unlink',
    'bulk',
    'revert',
    'restore_value',
    'restore_record',
  ]),
  /** Object of the records (null when several objects are involved). */
  objectKey: z.string().nullable(),
  /** Records the operation changed. */
  count: z.number().int().nonnegative(),
  /** Field api keys that changed (at most 20). */
  fields: z.array(z.string()),
  /** New stage key of a stage change. */
  stage: z.string().nullable(),
});
export type UndoSummary = z.infer<typeof UndoSummary>;

/**
 * Returned by every reversible record mutation. `POST /api/v1/undo/:token` reverts it until
 * `expiresAt` (10 minutes); the token belongs to the member who made the change.
 */
export const UndoToken = z.object({
  token: Uuid,
  expiresAt: z.string(),
  /** Toast text in the member's language, e.g. "Moved to Proposal" or "35 Deals updated". */
  label: z.string(),
  summary: UndoSummary,
});
export type UndoToken = z.infer<typeof UndoToken>;

/** A written record plus the undo token of the write (absent when nothing changed). */
export const RecordWriteResult = RecordDTO.extend({ undo: UndoToken.optional() });
export type RecordWriteResult = z.infer<typeof RecordWriteResult>;

export const RecordDeleteResult = z.object({
  /** The record and every record deleted with it (relations with `on delete: cascade`). */
  deleted: z.array(Uuid),
  undo: UndoToken.optional(),
});
export type RecordDeleteResult = z.infer<typeof RecordDeleteResult>;

export const LinkWriteResult = RecordLink.extend({ undo: UndoToken.optional() });
export type LinkWriteResult = z.infer<typeof LinkWriteResult>;

export const UnlinkResult = z.object({ undo: UndoToken.optional() });
export type UnlinkResult = z.infer<typeof UnlinkResult>;

/**
 * `error.details` of `409 version_conflict`: the record changed since `expectedVersion` in a
 * field the write touches. `current` is the record now, `changedFields` every field changed since,
 * `conflictingFields` the ones the write also changes (for "Keep yours / Take theirs").
 */
export const VersionConflictDetails = z.object({
  objectKey: z.string(),
  recordId: Uuid,
  expectedVersion: z.number().int(),
  currentVersion: z.number().int(),
  changedFields: z.array(z.string()),
  conflictingFields: z.array(z.string()),
  /** The latest change since `expectedVersion` (who, when). */
  lastChange: z
    .object({
      actor: z.object({ type: z.string(), id: z.string().nullable(), label: z.string().nullable() }),
      at: z.string(),
    })
    .nullable(),
  /** The record as it is now; null when it is deleted. */
  current: RecordDTO.nullable(),
});
export type VersionConflictDetails = z.infer<typeof VersionConflictDetails>;

/** Why a change was written (`record_changes.origin`). */
export const RecordChangeOrigin = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('bulk'), action: z.string() }),
  /** Reverts operation `of` (an undo; a redo reverts the undo). */
  z.object({ kind: z.literal('undo'), token: Uuid, of: Uuid }),
  z.object({
    kind: z.literal('restore_value'),
    changeId: Uuid,
    field: z.string(),
    value: z.enum(['before', 'after']),
  }),
  z.object({ kind: z.literal('restore_record'), at: z.string().nullable(), changeId: Uuid.nullable() }),
  /**
   * Written by AI on the member's behalf (an AI field fill, an approved Copilot change): history reads
   * "AI · <model> for <member>".
   */
  z.object({
    kind: z.literal('ai'),
    model: z.string().max(200).optional(),
    feature: z.enum(['field', 'copilot']),
    mode: z.enum(['manual', 'batch', 'auto']).optional(),
  }),
]);
export type RecordChangeOrigin = z.infer<typeof RecordChangeOrigin>;

/** History entries carry the operation and origin of their write. */
export const RecordChangeEntry = RecordChange.extend({
  operationId: Uuid.nullable(),
  origin: RecordChangeOrigin.nullable(),
});
export type RecordChangeEntry = z.infer<typeof RecordChangeEntry>;

const FieldValues = z
  .record(z.string(), z.unknown())
  .refine((v) => Object.keys(v).length > 0, { message: 'Set at least one field' })
  .refine((v) => Object.keys(v).length <= 200, { message: 'At most 200 fields' });
const FieldKeyList = z.array(z.string().min(1).max(63)).min(1).max(200);
const FieldKey = z.string().min(1).max(63);

/** Which records a bulk operation changes: ids, or everything a filter (+ search) matches. */
export const BulkSelection = z.union([
  z.object({ ids: z.array(Uuid).min(1).max(BULK_MAX_IDS) }).strict(),
  z
    .object({
      /** null = every record the member can read. */
      filter: FilterNodeSchema.nullable(),
      q: z.string().max(200).optional(),
      excludeIds: z.array(Uuid).max(BULK_MAX_IDS).optional(),
    })
    .strict(),
]);
export type BulkSelection = z.infer<typeof BulkSelection>;

export const BulkCellRow = z
  .object({ id: Uuid, expectedVersion: ExpectedVersion, values: FieldValues })
  .strict();
export type BulkCellRow = z.infer<typeof BulkCellRow>;

const ListItems = z.array(z.unknown()).min(1).max(1000);
const TargetIds = z.array(Uuid).min(1).max(1000);

export const BulkAction = z.discriminatedUnion('kind', [
  /** Set the same values on every record. */
  z.object({ kind: z.literal('set'), values: FieldValues }).strict(),
  /** Empty fields. */
  z.object({ kind: z.literal('clear'), fields: FieldKeyList }).strict(),
  /** Add items to a list-valued field (multi-select, files, many-relation). */
  z.object({ kind: z.literal('add'), field: FieldKey, values: ListItems }).strict(),
  /** Remove items from a list-valued field. */
  z.object({ kind: z.literal('remove'), field: FieldKey, values: ListItems }).strict(),
  /** Link every record to the targets through a relation field (api key). */
  z.object({ kind: z.literal('link'), relation: FieldKey, targetIds: TargetIds }).strict(),
  z.object({ kind: z.literal('unlink'), relation: FieldKey, targetIds: TargetIds }).strict(),
  /** Soft delete (restorable). */
  z.object({ kind: z.literal('delete') }).strict(),
  /** Restore soft-deleted records (select them by ids). */
  z.object({ kind: z.literal('restore') }).strict(),
  /**
   * Create a copy of every record (views-filters-bulk): the values a member can type, without
   * unique, calculated and generated values; the title gets a "(copy)" suffix. Undo deletes them.
   */
  z.object({ kind: z.literal('duplicate') }).strict(),
  /** Paste: per-record values with the version each row was read at. */
  z.object({ kind: z.literal('cells'), rows: z.array(BulkCellRow).min(1).max(BULK_MAX_IDS) }).strict(),
]);
export type BulkAction = z.infer<typeof BulkAction>;
export type BulkActionKind = BulkAction['kind'];

export const BulkMutationInput = z
  .object({
    /** Required except for `cells`, whose rows name their records. */
    selection: BulkSelection.optional(),
    action: BulkAction,
    /** Check every record and report the results without writing (at most 1,000 records). */
    dryRun: z.boolean().default(false),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.action.kind !== 'cells' && !v.selection)
      ctx.addIssue({ code: 'custom', path: ['selection'], message: 'Select records by ids or by filter' });
    if (v.action.kind === 'cells' && v.selection)
      ctx.addIssue({
        code: 'custom',
        path: ['selection'],
        message: 'Cells name their records; leave out selection',
      });
    if (v.action.kind === 'restore' && v.selection && !('ids' in v.selection))
      ctx.addIssue({ code: 'custom', path: ['selection'], message: 'Select deleted records by ids' });
  });
export type BulkMutationInput = z.input<typeof BulkMutationInput>;

/** Typed per-record failure (`params` feed the client's message, `field` the cell to mark). */
export const BulkRecordError = z.object({
  code: ErrorCode,
  message: z.string(),
  params: z.record(z.string(), z.unknown()),
  field: z.string().optional(),
});
export type BulkRecordError = z.infer<typeof BulkRecordError>;

export const BulkRecordResult = z.object({
  id: Uuid,
  ok: z.boolean(),
  /** Version after the write. */
  version: z.number().int().optional(),
  /** Nothing to change for this record (already in the requested state). */
  unchanged: z.boolean().optional(),
  error: BulkRecordError.optional(),
  /** Undo only: fields left as they are because someone changed them since. */
  skippedFields: z.array(z.string()).optional(),
  /** Undo only: fields left as they are because the member may no longer change them. */
  deniedFields: z.array(z.string()).optional(),
  /** `duplicate` only: the id of the new copy. */
  createdId: Uuid.optional(),
});
export type BulkRecordResult = z.infer<typeof BulkRecordResult>;

export const BulkMutationResult = z.object({
  /** One entry per record (empty while a job runs: see `jobId`). */
  results: z.array(BulkRecordResult),
  /** Records selected. */
  total: z.number().int(),
  succeeded: z.number().int(),
  failed: z.number().int(),
  dryRun: z.boolean(),
  /** A dry run over more than 1,000 records checks the first 1,000. */
  truncated: z.boolean(),
  /** One token reverts every record of the operation. */
  undo: UndoToken.optional(),
  /** Above 1,000 records: follow `GET /jobs/:id`. */
  jobId: Uuid.optional(),
});
export type BulkMutationResult = z.infer<typeof BulkMutationResult>;

export const UndoResult = z.object({
  status: z.enum(['done', 'partial', 'failed', 'queued']),
  results: z.array(BulkRecordResult),
  reverted: z.number().int(),
  failed: z.number().int(),
  /** Reverts this undo (redo). */
  redo: UndoToken.optional(),
  /** Large undo: follow `GET /jobs/:id`. */
  jobId: Uuid.optional(),
});
export type UndoResult = z.infer<typeof UndoResult>;

/** Progress of a background record operation (bulk write or large undo). */
export const RecordJobDTO = z.object({
  id: Uuid,
  kind: z.enum(['bulk', 'undo']),
  status: z.enum(['queued', 'running', 'succeeded', 'failed']),
  objectKey: z.string().nullable(),
  /** Bulk action kind, or `undo`. */
  action: z.string(),
  total: z.number().int(),
  processed: z.number().int(),
  succeeded: z.number().int(),
  failed: z.number().int(),
  /** Failed records (the first 100). */
  failures: z.array(BulkRecordResult),
  /** A finished bulk job: reverts the whole operation. */
  undo: UndoToken.nullable(),
  /** A finished undo job: reverts the undo. */
  redo: UndoToken.nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type RecordJobDTO = z.infer<typeof RecordJobDTO>;

/** "Restore this value" on a history entry: the field's value before (default) or after it. */
export const RestoreValueInput = z
  .object({
    field: FieldKey,
    value: z.enum(['before', 'after']).default('before'),
    expectedVersion: ExpectedVersion.optional(),
  })
  .strict();
export type RestoreValueInput = z.input<typeof RestoreValueInput>;

/** "Restore record to this point": every field as of an instant, or right after a change. */
export const RestoreRecordInput = z.union([
  z.object({ at: z.iso.datetime({ offset: true }), expectedVersion: ExpectedVersion.optional() }).strict(),
  z.object({ changeId: Uuid, expectedVersion: ExpectedVersion.optional() }).strict(),
]);
export type RestoreRecordInput = z.input<typeof RestoreRecordInput>;
